import type { FilterType } from '../types/filter-meta';
import type {
  CheckboxFilterValue,
  DateFilterValue,
  DateTimeFilterValue,
  FilterValue,
  NumberFilterValue,
  SelectFilterValue,
  TextFilterValue,
  TimeFilterValue,
} from '../types/filter-values';

/**
 * Type guard to check if a filter value is a SelectFilterValue
 */
export function isSelectFilterValue(value: FilterValue): value is SelectFilterValue {
  return (
    ('operator' in value && value.operator === 'isEmpty') ||
    ('operator' in value && (value.operator === 'anyOf' || value.operator === 'noneOf') && 'values' in value)
  );
}

/**
 * Type guard to check if a filter value is a TextFilterValue
 */
export function isTextFilterValue(value: FilterValue): value is TextFilterValue {
  return (
    ('operator' in value && value.operator === 'isEmpty') ||
    ('operator' in value &&
      (value.operator === 'contains' ||
        value.operator === 'equals' ||
        value.operator === 'startsWith' ||
        value.operator === 'endsWith') &&
      'value' in value)
  );
}

/**
 * Type guard to check if a filter value is a NumberFilterValue
 */
export function isNumberFilterValue(value: FilterValue): value is NumberFilterValue {
  return (
    ('operator' in value && value.operator === 'isEmpty') ||
    ('operator' in value &&
      (value.operator === 'eq' ||
        value.operator === 'gt' ||
        value.operator === 'gte' ||
        value.operator === 'lt' ||
        value.operator === 'lte') &&
      'value' in value) ||
    ('operator' in value && value.operator === 'between' && 'min' in value && 'max' in value)
  );
}

function hasScalarOrRangeStringOperator(value: FilterValue): boolean {
  return (
    ('operator' in value && value.operator === 'isEmpty') ||
    ('operator' in value &&
      (value.operator === 'eq' || value.operator === 'before' || value.operator === 'after') &&
      'value' in value &&
      typeof value.value === 'string') ||
    ('operator' in value &&
      value.operator === 'between' &&
      'start' in value &&
      'end' in value &&
      typeof value.start === 'string' &&
      typeof value.end === 'string')
  );
}

export function isDateFilterValue(value: FilterValue): value is DateFilterValue {
  return hasScalarOrRangeStringOperator(value);
}

export function isTimeFilterValue(value: FilterValue): value is TimeFilterValue {
  return hasScalarOrRangeStringOperator(value);
}

export function isDateTimeFilterValue(value: FilterValue): value is DateTimeFilterValue {
  return hasScalarOrRangeStringOperator(value);
}

/**
 * Type guard to check if a filter value is a CheckboxFilterValue
 */
export function isCheckboxFilterValue(value: FilterValue): value is CheckboxFilterValue {
  return (
    'operator' in value &&
    (value.operator === 'is' || value.operator === 'isNot') &&
    'value' in value &&
    typeof value.value === 'boolean'
  );
}

/**
 * Validates that a filter value matches the expected type based on filter type.
 * Returns the appropriate type guard result based on the filter type.
 */
export function isValidFilterValue(filterType: FilterType, value: FilterValue): boolean {
  switch (filterType) {
    case 'select':
    case 'tag':
      return isSelectFilterValue(value);
    case 'text':
      return isTextFilterValue(value);
    case 'number':
    case 'currency':
      return isNumberFilterValue(value);
    case 'date':
      return isDateFilterValue(value);
    case 'time':
      return isTimeFilterValue(value);
    case 'datetime':
      return isDateTimeFilterValue(value);
    case 'checkbox':
      return isCheckboxFilterValue(value);
    default:
      return false;
  }
}
